import { z } from 'zod'
import { paginacaoSchema } from './comum'

/**
 * Os grupos do filtro "O que" da tela de Logs (mapa de uso, 08/10/2026).
 *
 * O Mike pediu os botões Pessoas e Sistema e filtros melhores, "porque
 * existem coisas do sistema além da conexão". O grupo sai da entidade do
 * registro, e a entrada no sistema fica à parte, pela ação: login e logout
 * também são da entidade "usuario", e misturados com permissões escondiam
 * quem mexeu na equipe.
 */
export const GRUPOS_DE_LOG = [
  {
    id: 'entrada',
    rotulo: 'Entrada e senha',
    acoes: [
      'login',
      'login_negado',
      'login_freado',
      'logout',
      'trocar_senha',
      'redefinir_senha',
      'manutencao_sessoes',
    ],
    entidades: ['sessao'],
  },
  {
    id: 'equipe',
    rotulo: 'Equipe e permissões',
    acoes: [],
    entidades: ['usuario', 'preset_de_permissao', 'setor', 'meta'],
  },
  {
    id: 'conversas',
    rotulo: 'Conversas',
    acoes: [],
    entidades: [
      'chamado',
      'mensagem',
      'mensagem_agendada',
      'lead_anuncio',
      'notificacao',
      'tarefa',
    ],
  },
  {
    id: 'contatos',
    rotulo: 'Contatos e empresas',
    acoes: [],
    entidades: ['contato', 'empresa'],
  },
  {
    id: 'vendas',
    rotulo: 'Vendas',
    acoes: [],
    entidades: ['negociacao'],
  },
  {
    id: 'conexao',
    rotulo: 'Conexão do WhatsApp',
    acoes: [],
    entidades: ['conexao'],
  },
  {
    id: 'configuracao',
    rotulo: 'Configuração',
    acoes: [],
    entidades: [
      'configuracao',
      'funil',
      'etapa',
      'tag',
      'motivo_perda',
      'campo',
      'chatbot_fluxo',
      'resposta_rapida',
      'material',
      'pasta_material',
      'aviso_do_sistema',
      'baixa_pendencia',
      'sistema',
      'log',
    ],
  },
  {
    id: 'planilhas',
    rotulo: 'Planilhas e relatórios',
    acoes: [],
    entidades: ['planilha', 'estatisticas'],
  },
  /*
    Os dois produtos que vieram depois do mapa de uso (ADR 0057 e 0063 a 0066).
    Sem grupo, o que se fez neles só aparecia no "Tudo" (ADR 0075).
  */
  {
    id: 'marketing',
    rotulo: 'Marketing',
    acoes: [],
    entidades: ['marca_marketing', 'perfil_social', 'midia_marketing', 'post_marketing', 'conexao_rede'],
  },
  {
    id: 'projetos',
    rotulo: 'Projetos e ideias',
    acoes: [],
    entidades: ['cartao_projeto', 'diagrama_ideias', 'bloco_ideia'],
  },
] as const

export type GrupoDeLog = (typeof GRUPOS_DE_LOG)[number]['id']

export const filtroLogsSchema = paginacaoSchema.extend({
  usuarioId: z.string().uuid().optional(),
  acao: z.string().trim().max(60).optional(),
  entidade: z.string().trim().max(60).optional(),
  de: z.coerce.date().optional(),
  ate: z.coerce.date().optional(),
  /** Pessoas (alguém fez) ou Sistema (o próprio CRM fez). Vazio é tudo. */
  origem: z.enum(['pessoas', 'sistema']).optional(),
  grupo: z.enum(GRUPOS_DE_LOG.map((g) => g.id) as [GrupoDeLog, ...GrupoDeLog[]]).optional(),
  /** Parte do nome do cliente: os registros dele, das conversas e das negociações dele. */
  cliente: z.string().trim().max(80).optional(),
})

/**
 * Limpeza por intervalo (5.7). Exige as duas pontas de propósito:
 * a especificação quer evitar apagar tudo de uma vez.
 */
export const limparLogsSchema = z
  .object({
    de: z.coerce.date(),
    ate: z.coerce.date(),
  })
  .refine((d) => d.de <= d.ate, {
    message: 'a data inicial precisa ser anterior à final',
    path: ['de'],
  })

export interface LogDto {
  id: string
  usuarioId: string | null
  usuarioNome: string | null
  acao: string
  entidade: string
  entidadeId: string | null
  dadosAntes: unknown
  dadosDepois: unknown
  ip: string | null
  criadoEm: string
}

/** Como cada ação aparece na tela, em vez do nome interno (mapa de uso, 08/10/2026). */
const ROTULO_ACAO_DE_LOG: Record<string, string> = {
  login: 'Entrou',
  login_negado: 'Senha errada',
  login_freado: 'Entrada bloqueada por tentativas',
  login_fora_do_horario: 'Tentou entrar fora do horário',
  desconectado_pelo_horario: 'Desconectado no fim do horário',
  prorrogar_horario: 'Pediu mais 30 minutos',
  logout: 'Saiu',
  trocar_senha: 'Trocou a própria senha',
  redefinir_senha: 'Redefiniu a senha de alguém',
  manutencao_sessoes: 'Limpou sessões vencidas',
  criar_usuario: 'Criou usuário',
  editar_usuario: 'Editou usuário',
  desativar_usuario: 'Desativou usuário',
  desligar_usuario: 'Desligou usuário',
  definir_permissoes: 'Mudou permissões',
  definir_numeros_do_usuario: 'Mudou os números de alguém',
  definir_setores: 'Mudou os setores de alguém',
  definir_horario_de_trabalho: 'Mudou o horário de alguém',
  alterar_status: 'Mudou o status',
  alterar_preferencias: 'Mudou preferências',
  criar_preset_de_permissao: 'Criou preset de permissão',
  atualizar_preset_de_permissao: 'Atualizou preset de permissão',
  renomear_preset_de_permissao: 'Renomeou preset de permissão',
  apagar_preset_de_permissao: 'Apagou preset de permissão',
  abrir_chamado: 'Abriu conversa',
  enviar_mensagem: 'Mandou mensagem',
  enviar_modelo: 'Mandou modelo da API oficial',
  nota_interna: 'Comentário para a equipe',
  editar_mensagem: 'Editou mensagem',
  apagar_mensagem: 'Apagou mensagem',
  cancelar_envio_mensagem: 'Cancelou envio de mensagem',
  encerrar_chamado: 'Encerrou conversa',
  reabrir_chamado: 'Reabriu conversa',
  transferir_chamado: 'Transferiu conversa',
  pedir_apoio: 'Pediu apoio',
  aceitar_apoio: 'Aceitou apoio',
  remover_apoio: 'Tirou apoio',
  sair_do_apoio: 'Saiu do apoio',
  agendar_retorno: 'Marcou retorno',
  cancelar_retorno: 'Cancelou retorno',
  agendar_mensagem: 'Programou mensagem',
  cancelar_agendamento: 'Cancelou mensagem programada',
  restaurar_agendamento: 'Desfez cancelamento de mensagem programada',
  disparar_agendamento: 'Mensagem programada saiu',
  qualificar_lead_anuncio: 'Qualificou lead de anúncio',
  envio_nao_entregue: 'Mensagem não chegou ao cliente',
  criar_contato: 'Criou contato',
  criar_contato_whatsapp: 'Contato novo pelo WhatsApp',
  editar_contato: 'Editou contato',
  excluir_contato: 'Excluiu contato',
  definir_etiquetas: 'Mudou etiquetas',
  telefone_repetido_oculto: 'Tentou cadastrar número que já existe',
  criar_empresa: 'Criou empresa',
  ligar_contato_empresa: 'Ligou contato a uma empresa',
  soltar_contato_empresa: 'Tirou contato da empresa',
  excluir_empresa: 'Excluiu empresa',
  nova_negociacao: 'Abriu negociação',
  mover_etapa: 'Mudou etapa',
  registrar_ganho: 'Registrou venda',
  registrar_perda: 'Registrou perda',
  reabrir_negociacao: 'Reabriu negociação',
  conexao_status: 'Estado da conexão',
  conexao_reconectando: 'Conexão reconectando',
  conferencia_mensagens: 'Conferência das mensagens',
  importar_historico: 'Importou histórico',
  importar_historico_inicio: 'Começou a importar histórico',
  criar_conexao: 'Cadastrou número',
  excluir_conexao: 'Excluiu número',
  editar_configuracao: 'Mudou regra do sistema',
  exportar_base: 'Exportou contatos',
  importar_base: 'Importou contatos',
  exportar_relatorio: 'Exportou estatísticas',
  limpar_logs: 'Apagou logs',
  // Pendências do Painel do admin.
  baixa_pendencia: 'Deu baixa em pendência',
  retornar_baixa_pendencia: 'Desfez baixa de pendência',
  apagar_baixa_pendencia: 'Tirou baixa do histórico',
  limpar_baixas_pendencia: 'Limpou o histórico de baixas',
  // FaroEscolar Marketing (ADR 0057 e 0058).
  criar_marca_marketing: 'Criou marca',
  editar_marca_marketing: 'Editou marca',
  excluir_marca_marketing: 'Apagou marca',
  criar_perfil_marketing: 'Criou perfil de rede social',
  editar_perfil_marketing: 'Editou perfil de rede social',
  excluir_perfil_marketing: 'Apagou perfil de rede social',
  conectar_rede_marketing: 'Conectou rede social',
  desconectar_rede_marketing: 'Desconectou rede social',
  subir_midia_marketing: 'Subiu foto ou vídeo de post',
  criar_post_marketing: 'Criou post',
  editar_post_marketing: 'Editou post',
  excluir_post_marketing: 'Apagou post',
  aprovar_post_marketing: 'Aprovou post',
  reprovar_post_marketing: 'Reprovou post',
  marcar_publicado_marketing: 'Marcou post como publicado',
  tentar_publicar_marketing: 'Pediu para publicar o post de novo',
  // Projetos (ADR 0063).
  criar_cartao_projeto: 'Criou balão em Projetos',
  editar_cartao_projeto: 'Editou balão em Projetos',
  mover_cartao_projeto: 'Moveu balão em Projetos',
  excluir_cartao_projeto: 'Apagou balão em Projetos',
  comentar_cartao_projeto: 'Comentou balão em Projetos',
  excluir_comentario_projeto: 'Apagou comentário em Projetos',
  anexar_arquivo_projeto: 'Anexou arquivo em Projetos',
  excluir_arquivo_projeto: 'Apagou arquivo em Projetos',
  dar_joinha_projeto: 'Deu joinha em Projetos',
  tirar_joinha_projeto: 'Tirou joinha em Projetos',
  // Ideias (ADR 0064 a 0066).
  salvar_diagrama_ideias: 'Mexeu no diagrama de Ideias',
  dar_joinha_ideia: 'Deu joinha numa ideia',
  tirar_joinha_ideia: 'Tirou joinha de uma ideia',
  aprovar_ideia: 'Aprovou ideia',
  reprovar_ideia: 'Reprovou ideia',
  tirar_aprovacao_ideia: 'Tirou a decisão de uma ideia',
}

/**
 * O "Sobre" da tela de Logs: o assunto do registro em palavras. O que não está
 * aqui aparece legível do mesmo jeito, sem o sublinhado.
 */
const ROTULO_ENTIDADE_DE_LOG: Record<string, string> = {
  usuario: 'usuário',
  sessao: 'sessão',
  preset_de_permissao: 'preset de permissão',
  chamado: 'conversa',
  mensagem_agendada: 'mensagem programada',
  lead_anuncio: 'lead de anúncio',
  notificacao: 'notificação',
  negociacao: 'negociação',
  conexao: 'número',
  configuracao: 'configuração',
  motivo_perda: 'motivo de perda',
  chatbot_fluxo: 'chatbot',
  resposta_rapida: 'resposta rápida',
  pasta_material: 'pasta de materiais',
  aviso_do_sistema: 'aviso do sistema',
  baixa_pendencia: 'baixa de pendência',
  estatisticas: 'estatísticas',
  marca_marketing: 'marca',
  perfil_social: 'perfil de rede social',
  midia_marketing: 'mídia de post',
  post_marketing: 'post',
  conexao_rede: 'conexão com rede social',
  cartao_projeto: 'balão de Projetos',
  diagrama_ideias: 'diagrama de Ideias',
  bloco_ideia: 'quadrado de Ideias',
}

export function rotuloDaEntidadeDeLog(entidade: string): string {
  return ROTULO_ENTIDADE_DE_LOG[entidade] ?? entidade.replace(/_/g, ' ')
}

/** O nome da ação para a tela; a que ainda não tem nome aparece legível do mesmo jeito. */
export function rotuloDaAcaoDeLog(acao: string): string {
  const conhecido = ROTULO_ACAO_DE_LOG[acao]
  if (conhecido) return conhecido
  const texto = acao.replace(/_/g, ' ')
  return texto.charAt(0).toUpperCase() + texto.slice(1)
}
